"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TicketService = void 0;
const errors_1 = require("../errors");
const service_1 = require("./service");
const mapper_1 = require("../utils/mapper");
const forum_1 = require("../models/forum");
class TicketMapper {
    static mapSummary(t) {
        const result = mapper_1.PdaMapper.map(t, TicketMapper.SummarySchema);
        mapper_1.PdaVerifier.verify(result, 'TicketSummary');
        return result;
    }
    static mapDetail(ticketId, data) {
        const result = mapper_1.PdaMapper.map(data, TicketMapper.DetailSchema);
        result.id = ticketId;
        mapper_1.PdaVerifier.verify(result, 'TicketDetail');
        return result;
    }
    static mapHistory(raw) {
        const result = mapper_1.PdaMapper.map(raw, TicketMapper.HistorySchema);
        mapper_1.PdaVerifier.verify(result, 'TicketHistory');
        return result;
    }
}
TicketMapper.UserSchema = {
    id: 0,
    name: 1
};
TicketMapper.SummarySchema = {
    id: 0,
    status: 1,
    title: 2,
    description: 2,
    kind: 6,
    author: { index: [7, 8], schema: TicketMapper.UserSchema },
    moderator: { index: [9, 10], schema: TicketMapper.UserSchema },
    context: {
        index: [3, 4, 12, 13],
        map: (v) => ({
            forum: { id: v[0], title: v[1] },
            topic: { id: v[2], postId: v[3] }
        })
    },
    stats: { index: 11, map: (v) => ({ commentsCount: v }) },
    dates: { index: [5], map: (v) => ({ created: v[0], updated: v[0] }) },
    flags: 14
};
TicketMapper.CommentSchema = {
    id: 0,
    createdAt: 1,
    content: 4,
    author: { index: [2, 3], schema: TicketMapper.UserSchema },
    flags: 5,
    ip: 6
};
TicketMapper.DetailSchema = {
    id: -1, // Passed externally
    status: 0,
    title: 1,
    description: 1,
    kind: 5,
    author: { index: [6, 7], schema: TicketMapper.UserSchema },
    moderator: { index: [8, 9], schema: TicketMapper.UserSchema },
    context: {
        index: [2, 3, 12, 13],
        map: (v) => ({
            forum: { id: v[0], title: v[1] },
            topic: { id: v[2], postId: v[3] }
        })
    },
    stats: { index: [11], map: (v) => ({ commentsCount: v[0]?.length || 0 }) },
    dates: { index: [4], map: (v) => ({ created: v[0], updated: v[0] }) },
    comments: { index: 11, list: TicketMapper.CommentSchema },
    extra: 10,
    flags: 14
};
TicketMapper.HistorySchema = {
    actionId: 0,
    timestamp: 1,
    responsible: { index: [2, 3], schema: TicketMapper.UserSchema }
};
class TicketService extends service_1.Service {
    constructor(transport, auth) {
        super(transport, auth);
    }
    /**
     * Get list of tickets
     */
    async getTickets(offset = 0, limit = 20, forumId, topicId, onlyMine = false, sortByForum = false) {
        let flags = 0;
        if (sortByForum)
            flags |= 1;
        if (onlyMine)
            flags |= 4;
        let targetId = 0;
        if (forumId) {
            flags |= 8;
            targetId = forumId;
        }
        else if (topicId) {
            flags |= 16;
            targetId = topicId;
        }
        const response = await this.transport.send('tl', [
            offset,
            limit,
            flags,
            [targetId]
        ]);
        this.checkError(response, 'tl');
        const total = response.data[0];
        const rawTickets = response.data[1] || [];
        return {
            total,
            tickets: rawTickets.map(TicketMapper.mapSummary)
        };
    }
    /**
     * Get ticket content and comments
     */
    async getTicket(ticketId) {
        const response = await this.transport.send('tc', [ticketId]);
        this.checkError(response, 'tc');
        return TicketMapper.mapDetail(ticketId, response.data);
    }
    /**
     * Get ticket history
     */
    async getHistory(ticketId) {
        const response = await this.transport.send('th', [ticketId]);
        this.checkError(response, 'th');
        const rawHistory = response.data[0] || [];
        return rawHistory.map(h => TicketMapper.mapHistory(h));
    }
    /**
     * Change ticket status
     * @param status 0: New, 1: In Work, 2: Done
     * @param responsibleId ID текущего ответственного по тикету (0 — никто не назначен)
     */
    async setStatus(ticketId, status, responsibleId = 0) {
        const res = await this.transport.send('ts', [ticketId, status, responsibleId]);
        if (res.status === 5 && Array.isArray(res.data) && res.data[2] === 4) {
            const handlerName = typeof res.data[3] === 'string' ? res.data[3] : '';
            const msg = handlerName
                ? `Тикет уже в работе у модератора ${handlerName}`
                : 'Тикет уже взят другим модератором';
            throw new errors_1.FourPdaError(5, 'ts', msg, res.data);
        }
        this.checkError(res, 'ts');
    }
    /**
     * Add a comment to the ticket
     */
    async addComment(ticketId, text) {
        const response = await this.transport.send('tm', [ticketId, 0, text]);
        this.checkError(response, 'tm');
        return response.data[0];
    }
    /**
     * Edit an existing comment
     */
    async editComment(ticketId, commentId, text) {
        const res = await this.transport.send('tm', [ticketId, commentId, text]);
        this.checkError(res, 'tm');
    }
    /**
     * Delete a comment
     */
    async deleteComment(ticketId, commentId) {
        const res = await this.transport.send('td', [ticketId, commentId]);
        this.checkError(res, 'td');
    }
    /**
     * Create a new ticket (Send a complaint/report)
     * Command: "bc" (CREATE_TICKET)
     * @param type 0 = forum post, 1 = QMS message, 2 = article comment
     */
    async createTicket(topicId, postId, text, type = 0) {
        const targetId = type === 0 ? postId : topicId;
        const response = await this.transport.send('bc', [type, targetId, text]);
        let status = forum_1.ReportStatus.UNKNOWN_ERROR;
        let message = 'Неизвестная ошибка';
        const rawStatus = response.status;
        if (rawStatus === 0) {
            status = forum_1.ReportStatus.SUCCESS;
            message = 'Жалоба успешно отправлена и тикет создан';
        }
        else if (rawStatus === 2) {
            status = forum_1.ReportStatus.LIMIT_ERROR;
            message = 'Ошибка ограничений (жалоба на этот пост уже создана и находится на рассмотрении, или флуд-контроль)';
        }
        else if (rawStatus === 3) {
            status = forum_1.ReportStatus.NOT_FOUND;
            message = 'Целевой объект не найден';
        }
        else if (rawStatus === 4) {
            status = forum_1.ReportStatus.TOO_SHORT;
            message = 'Текст жалобы слишком короткий';
        }
        else {
            this.checkError(response, 'bc');
        }
        return {
            status,
            message,
            _raw: response.data
        };
    }
}
exports.TicketService = TicketService;
